/**
 * 画布本地快照存取（localStorage）
 *
 * 用途：自由画板草稿的崩溃兜底。后端自动保存有秒级窗口，浏览器崩溃时
 * beforeunload/异步请求均不可靠，唯一能在关闭/崩溃前完成的是同步
 * localStorage 写入。恢复时与后端草稿比较，取未同步的增量版本。
 *
 * 两个 key：
 * - creation_canvas_snapshot:{userId}  本地快照 { data, savedAt }
 * - creation_canvas_last_sync:{userId} 最后一次后端保存成功时刻 { syncedAt }
 *
 * 判定依据全部使用客户端时钟（savedAt vs syncedAt），不与服务器时间
 * draft.updateTime 直接比较——客户端与服务器时钟偏差会造成误判。
 */

const SNAPSHOT_KEY_PREFIX = 'creation_canvas_snapshot:'
const LAST_SYNC_KEY_PREFIX = 'creation_canvas_last_sync:'

/**
 * 本地快照判定为“有未同步增量”的最小时间差（毫秒）。
 * 快照在编辑后 ~1s 落盘、后端保存 4s 防抖：后端刚同步完又有新编辑时，
 * 差值只有 ~1s 多，阈值需压到 1.5s 才能识别这批“紧随同步的增量”
 */
export const SNAPSHOT_DELTA_MS = 1500

export const getSnapshotKey = (userId) => `${SNAPSHOT_KEY_PREFIX}${userId}`
export const getLastSyncKey = (userId) => `${LAST_SYNC_KEY_PREFIX}${userId}`

/**
 * 读取本地快照
 * @returns {{ data: Object, savedAt: number } | null}
 */
export const readLocalSnapshot = (userId) => {
  if (!userId) return null
  try {
    const raw = localStorage.getItem(getSnapshotKey(userId))
    if (!raw) return null
    const snapshot = JSON.parse(raw)
    if (!snapshot || typeof snapshot.savedAt !== 'number' || !snapshot.data) return null
    return snapshot
  } catch (e) {
    console.warn('[画布快照] 读取失败:', e)
    return null
  }
}

/**
 * 同步写入本地快照。localStorage 写满等异常自动降级（仅后端保存），不抛出。
 * @param {Object} data 已序列化好的画布状态对象
 */
export const writeLocalSnapshot = (userId, data) => {
  if (!userId) return
  try {
    localStorage.setItem(getSnapshotKey(userId), JSON.stringify({
      data,
      savedAt: Date.now()
    }))
  } catch (e) {
    // QuotaExceededError 等：跳过本地快照，保持后端保存通道不受影响
    console.warn('[画布快照] 写入失败(已降级为仅后端保存):', e)
  }
}

/**
 * 本地快照是否包含未同步到后端的增量
 */
export const hasUnsyncedDelta = (userId) => {
  const snapshot = readLocalSnapshot(userId)
  if (!snapshot) return false
  const syncedAt = readLastSync(userId)
  return snapshot.savedAt - syncedAt >= SNAPSHOT_DELTA_MS
}

/**
 * 读取最后一次后端保存成功的客户端时刻（无记录返回 0）
 * @returns {number}
 */
export const readLastSync = (userId) => {
  if (!userId) return 0
  try {
    const raw = localStorage.getItem(getLastSyncKey(userId))
    if (!raw) return 0
    const record = JSON.parse(raw)
    return typeof record.syncedAt === 'number' ? record.syncedAt : 0
  } catch (e) {
    return 0
  }
}

/**
 * 记录后端保存成功：更新同步时刻并清除本地快照（收敛回单源）
 */
export const markBackendSynced = (userId) => {
  if (!userId) return
  try {
    localStorage.setItem(getLastSyncKey(userId), JSON.stringify({ syncedAt: Date.now() }))
    localStorage.removeItem(getSnapshotKey(userId))
  } catch (e) {
    console.warn('[画布快照] 记录同步状态失败:', e)
  }
}

/**
 * 清除指定用户的全部本地草稿痕迹（快照 + 同步时刻），丢弃草稿/清空画布时使用
 */
export const clearLocalDraftTraces = (userId) => {
  if (!userId) return
  try {
    localStorage.removeItem(getSnapshotKey(userId))
    localStorage.removeItem(getLastSyncKey(userId))
  } catch (e) {
    // 忽略
  }
}

/**
 * 按前缀清除所有用户的画布本地快照与同步记录，登出时使用（隐私清理）
 */
export const clearAllCanvasSnapshots = () => {
  try {
    const keys = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && (key.startsWith(SNAPSHOT_KEY_PREFIX) || key.startsWith(LAST_SYNC_KEY_PREFIX))) {
        keys.push(key)
      }
    }
    keys.forEach(key => localStorage.removeItem(key))
  } catch (e) {
    console.warn('[画布快照] 清理失败:', e)
  }
}
